<template>
  <div class="users-hours text-dark">
    <div class="users-hours__header">
      <h2>Arbeidsplan for {{ numberOfWorkDaysInPeriod }} arbejdsdage</h2>
      <div class="user-tasks__header__right">
        <p class="d-link" @click="resetDates" v-if="!isDatesPristine">{{ $t('vue2.task_overview.reset') }}</p>
        <DatePickerRangeShortcuts
          v-if="activeSchoolYear"
          :startDate="activeSchoolYear.employee_time_start"
          :endDate="activeSchoolYear.employee_time_end"
          :selectedStartDate="startDate"
          :selectedEndDate="endDate"
          class="marg-1-right"
          @shortcutSelected="setDatesFromRange">
        </DatePickerRangeShortcuts>
        <DatePickerRange
          :startdate="startDate"
          :enddate="endDate"
          @startchanged="setStartDate"
          @endchanged="setEndDate">
        </DatePickerRange>
        <button class="marg-1-left bttn" :class="{'bttn--loading': isLoadingUpdatedNumbers}" @click="updateUserHours">
          {{ $t('vue2.assign_users_hours.update') }}
        </button>
      </div>
    </div>
    <div class="user-table-container">
      <div class="user-table-container__loader" @click.stop v-if="isLoadingUpdatedNumbers">
        <Loader :is-small="true"></Loader>
      </div>
      <table class="user-table">
        <thead>
        <tr>
          <thUser rowspan="3" sort-prop="name" :title="$t('vue2.assign_users_hours.name')" :activeSortKey="sortKey"
                  @sortby="sortBy"></thUser>
          <th colspan="3" class="working-hours">
            <div>{{ $t('vue2.assign_users_hours.work_hours') }}</div>
          </th>
          <th colspan="3" class="with-kids-color">
            <div>Med børn</div>
          </th>
          <th colspan="3" class="without-kids-color">
            <div>Uden børn</div>
          </th>
          <th colspan="3" class="working-hours">
            <div>Med + uden børn</div>
          </th>
        </tr>
        <tr>
          <!-- ARBEJDSTID -->
          <thUser sort-prop="v_total_hours_assigned_in_period" :title="$t('vue2.assign_users_hours.assigned')"
                  :activeSortKey="sortKey"
                  class="working-hours"
                  @sortby="sortBy"></thUser>
          <thUser sort-prop="v_total_hours_actual" :title="$t('vue2.assign_users_hours.scheduled')"
                  :activeSortKey="sortKey"
                  class="working-hours"
                  @sortby="sortBy"></thUser>
          <thUser sort-prop="v_total_hours_diff"
                  class="working-hours"
                  :title="$t('vue2.assign_users_hours.diff')" :activeSortKey="sortKey"
                  @sortby="sortBy"></thUser>
          <!-- Med børn -->
          <thUser sort-prop="v_with_kids_assigned_in_period" class="with-kids-color"
                  :title="$t('vue2.assign_users_hours.assigned')"
                  :activeSortKey="sortKey"
                  @sortby="sortBy"></thUser>
          <thUser sort-prop="v_with_kids_actual" class="with-kids-color"
                  :title="$t('vue2.assign_users_hours.scheduled')"
                  :activeSortKey="sortKey"
                  @sortby="sortBy"></thUser>
          <thUser sort-prop="v_with_kids_diff" class="with-kids-color" :title="$t('vue2.assign_users_hours.diff')"
                  :activeSortKey="sortKey"
                  @sortby="sortBy"></thUser>
          <!-- Uden børn -->
          <thUser sort-prop="v_without_kids_assigned_in_period" class="without-kids-color"
                  :title="$t('vue2.assign_users_hours.assigned')"
                  :activeSortKey="sortKey"
                  @sortby="sortBy"></thUser>
          <thUser sort-prop="v_without_kids_actual" class="without-kids-color"
                  :title="$t('vue2.assign_users_hours.scheduled')"
                  :activeSortKey="sortKey"
                  @sortby="sortBy"></thUser>
          <thUser sort-prop="v_without_kids_diff" class="without-kids-color" :title="$t('vue2.assign_users_hours.diff')"
                  :activeSortKey="sortKey"
                  @sortby="sortBy"></thUser>
          <!-- Med + uden børn -->
          <thUser sort-prop="v_with_and_without_kids_assigned_in_period" class="working-hours"
                  :title="$t('vue2.assign_users_hours.assigned')"
                  :activeSortKey="sortKey"
                  @sortby="sortBy"></thUser>
          <thUser sort-prop="v_with_and_without_kids_actual" class="working-hours"
                  :title="$t('vue2.assign_users_hours.scheduled')"
                  :activeSortKey="sortKey"
                  @sortby="sortBy"></thUser>
          <thUser sort-prop="v_with_and_without_kids_diff" class="working-hours"
                  :title="$t('vue2.assign_users_hours.diff')" :activeSortKey="sortKey"
                  @sortby="sortBy"></thUser>
        </tr>
        </thead>
        <tbody>
        <tr class="tr-user" v-for="user in sortedUsers" :key="user.id">
          <th class="tr-user__name">
            <div class="tr-user__name__inner">
              <p>{{ user.name }}</p>
              <div class="tr-user__name__bttn icon__bg-circle hint--right"
                   @click="setTheActiveUserInAssignHours(user)"
                   aria-label="Årsnormsoversigt">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" class="icon-chart tw-icon tw-icon--small">
                  <path class="primary"
                        d="M5 3h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5c0-1.1.9-2 2-2zm11 4a1 1 0 0 0-1 1v8a1 1 0 0 0 2 0V8a1 1 0 0 0-1-1z"/>
                  <path class="secondary"
                        d="M8 11a1 1 0 0 1 1 1v4a1 1 0 0 1-2 0v-4a1 1 0 0 1 1-1zm4-2a1 1 0 0 1 1 1v6a1 1 0 0 1-2 0v-6a1 1 0 0 1 1-1z"/>
                </svg>
              </div>
            </div>
          </th>
          <td class="number td-presence"
              :class="{'is-number--calculated': !isDatesPristine}"
              :content='createTooltip(user.v_assigned_total_hours)'
              v-tippy="{
                arrow: true,
                animateFill: false,
                animation : 'shift-away',
                zIndex: '999999992',
                boundary: 'window',
                onShow: () => !isDatesPristine
              }">
            <span class="icon-clock" v-if="!isDatesPristine"></span>
            {{ user.v_total_hours_assigned_in_period }}
          </td>
          <td class="number td-presence" :aria-label="user.v_total_hours_actual">
          </td>
          <td class="number td-presence number__scale-indicator" :style="scaleIndicator(user.v_total_hours_diff)"
              :aria-label="user.v_total_hours_diff">
          </td>
          <td class="number with-kids-color"
              :class="{'is-number--calculated': !isDatesPristine}"
              :content='createTooltip(user.v_total_with_kids)'
              v-tippy="{
                arrow: true,
                animateFill: false,
                animation : 'shift-away',
                zIndex: '999999992',
                boundary: 'window',
                onShow: () => !isDatesPristine
              }">
            <span class="icon-clock" v-if="!isDatesPristine"></span>
            {{ user.v_with_kids_assigned_in_period }}
          </td>
          <td class="number with-kids-color" :aria-label="user.v_with_kids_actual">
          </td>
          <td class="number with-kids-color number__scale-indicator" :style="scaleIndicator(user.v_with_kids_diff)"
              :aria-label="user.v_with_kids_diff">
          </td>
          <td class="number without-kids-color"
              :class="{'is-number--calculated': !isDatesPristine}"
              :content='createTooltip(user.v_total_without_kids)'
              v-tippy="{
                arrow: true,
                animateFill: false,
                animation : 'shift-away',
                zIndex: '999999992',
                boundary: 'window',
                onShow: () => !isDatesPristine
              }">
            <span class="icon-clock" v-if="!isDatesPristine"></span>
            {{ user.v_without_kids_assigned_in_period }}
          </td>
          <td class="number without-kids-color" :aria-label="user.v_without_kids_actual">
          </td>
          <td class="number without-kids-color number__scale-indicator"
              :style="scaleIndicator(user.v_without_kids_diff)"
              :aria-label="user.v_without_kids_diff">
          </td>
          <td class="number td-presence"
              :class="{'is-number--calculated': !isDatesPristine}"
              :content='createTooltip(user.v_assigned_total_hours)'
              v-tippy="{
                arrow: true,
                animateFill: false,
                animation : 'shift-away',
                zIndex: '999999992',
                boundary: 'window',
                onShow: () => !isDatesPristine
              }">
            <span class="icon-clock" v-if="!isDatesPristine"></span>
            {{ user.v_total_hours_assigned_in_period }}
          </td>
          <td class="number td-presence" :aria-label="user.v_with_and_without_kids_actual">
          </td>
          <td class="number td-presence number__scale-indicator"
              :style="scaleIndicator(user.v_with_and_without_kids_diff)"
              :aria-label="user.v_with_and_without_kids_diff">
          </td>
        </tr>
        </tbody>
      </table>
    </div>
    <DModal v-if="theUserToShowTasks" @close="closeTheUserToShowTasks" :isBgOverlay="true" :show-close="false">
      <div class="user-task__container" slot="insertComponent">
        <div class="user-task__container__header">
          <h2></h2>
          <div class="icon__bg-circle ww-modal__header__close" @click="closeTheUserToShowTasks">
            <span class="icon-cross2"></span>
          </div>
        </div>
        <UserTasks
          v-if="theUserToShowTasks"
          :initialStartDate="startDate"
          :initialEndDate="endDate"
          :user_id="theUserToShowTasks.id"
          :user_calendar_id="theUserToShowTasks.user_calendar_id"
          :user_name="theUserToShowTasks.name"
          :isShowPrintButton="false"
          :isCanSeeOtherUsers="false"
        ></UserTasks>
      </div>
    </DModal>
  </div>
</template>

<script>
import thUser from '../AssignHours/AssignHoursAllUsersTheadTh'
import moment from 'moment'
import docendo from '../../docendo'
import DatePickerRange from '../datepicker/DatePickerRange'
import Loader from '../common/Loader'
import DModal from '../common/DModal'
import UserTasks from '../User/UserTasks'
import DatePickerRangeShortcuts from '../datepicker/DatePickerRangeShortcuts'

export default {
  components: {DatePickerRangeShortcuts, Loader, thUser, DatePickerRange, DModal, UserTasks},
  props: {
    users: {
      type: Array,
      default: () => []
    },
    schoolId: {
      type: Number,
      default: null
    }
  },
  data () {
    return {
      sortKey: 'name',
      sortOrder: 1,
      isLoadingUpdatedNumbers: false,
      usersWithScheduledHours: {},
      startDate: moment(),
      endDate: moment(),
      allVacationsDays: [],
      theUserToShowTasks: null
    }
  },
  computed: {
    activeSchoolYear () {
      return this.$store.state.localActivatedSchoolYear
    },
    isDatesPristine () {
      return this.compareDates(this.activeSchoolYear.employee_time_start, this.startDate) && this.compareDates(this.activeSchoolYear.employee_time_end, this.endDate)
    },
    workDayDates () {
      const self = this

      function isNotWeekend (m) {
        return moment(m).isoWeekday() !== 6 && moment(m).isoWeekday() !== 7
      }

      function isNotVacationDay (m) {
        const idx = self.allVacationsDays.findIndex(date => moment(date.day).format('YYYY-MM-DD') === m)
        return idx < 0
      }

      let startDate = moment(this.activeSchoolYear.employee_time_start).format('YYYY-MM-DD')
      const endDate = moment(this.activeSchoolYear.employee_time_end).format('YYYY-MM-DD')
      const dateArray = []
      while (moment(startDate).isSameOrBefore(endDate)) {
        const m = moment(startDate).format('YYYY-MM-DD')
        if (isNotWeekend(m) && isNotVacationDay(m)) {
          dateArray.push(m)
        }
        startDate = moment(startDate).add(1, 'days')
      }
      return dateArray
    },
    workDayDatesInPeriod () {
      let startDate = moment(this.startDate).format('YYYY-MM-DD')
      const endDate = moment(this.endDate).format('YYYY-MM-DD')
      const dateArray = []
      while (moment(startDate).isSameOrBefore(endDate)) {
        const m = moment(startDate).format('YYYY-MM-DD')
        dateArray.push(m)
        startDate = moment(startDate).add(1, 'days')
      }
      return dateArray.filter(date => {
        const m = moment(date).format('YYYY-MM-DD')
        const idx = this.workDayDates.indexOf(m)
        return idx > -1
      })
    },
    numberOfWorkDays () {
      return this.workDayDates.length
    },
    numberOfWorkDaysInPeriod () {
      return this.workDayDatesInPeriod.length
    },
    allUsersInHash () {
      return this.$store.getters.allUsersInHash
    },
    usersWithProps () {
      return this.users.map(user => {
        const userHours = this.usersWithScheduledHours[user.id] || {}
        const totalHoursAssignedInPeriod = this.getAssignedHoursForPeriod(user.v_assigned_total_hours)
        const totalHoursActual = userHours.actual_total_hours || 0
        const totalHoursDiff = totalHoursAssignedInPeriod - totalHoursActual
        const withKidsAssignedForPeriod = this.getAssignedHoursForPeriod(user.v_total_with_kids)
        const withKidsHoursActual = userHours.actual_with_kids_hours || 0
        const withKidsDiff = withKidsAssignedForPeriod - withKidsHoursActual
        const withoutKidsAssignedForPeriod = this.getAssignedHoursForPeriod(user.v_total_without_kids)
        const withoutKidsHoursActual = userHours.actual_without_kids_hours || 0
        const withoutKidsDiff = withoutKidsAssignedForPeriod - withoutKidsHoursActual
        const withAndWithoutKidsAssignedForPeriod = this.getAssignedHoursForPeriod(user.v_assigned_total_hours)
        const withAndWithoutKidsHoursActual = withKidsHoursActual + withoutKidsHoursActual
        const withAndWithoutKidsDiff = withAndWithoutKidsAssignedForPeriod - withAndWithoutKidsHoursActual

        return {
          ...user,
          v_total_hours_assigned_in_period: this.numToFixed(totalHoursAssignedInPeriod),
          v_total_hours_actual: this.numToFixed(totalHoursActual),
          v_total_hours_diff: this.numToFixed(totalHoursDiff),
          v_with_kids_assigned_in_period: this.numToFixed(withKidsAssignedForPeriod),
          v_with_kids_actual: this.numToFixed(withKidsHoursActual),
          v_with_kids_diff: this.numToFixed(withKidsDiff),
          v_without_kids_assigned_in_period: this.numToFixed(withoutKidsAssignedForPeriod),
          v_without_kids_actual: this.numToFixed(withoutKidsHoursActual),
          v_without_kids_diff: this.numToFixed(withoutKidsDiff),
          v_with_and_without_kids_assigned_in_period: this.numToFixed(withAndWithoutKidsAssignedForPeriod),
          v_with_and_without_kids_actual: this.numToFixed(withAndWithoutKidsHoursActual),
          v_with_and_without_kids_diff: this.numToFixed(withAndWithoutKidsDiff)
        }
      })
    },
    sortedUsers () {
      const sortKey = this.sortKey
      const order = this.sortOrder
      const data = this.usersWithProps
      if (sortKey === 'name') {
        return this.$helpers.sortArrOnName(data)
      }
      return data.sort((a, b) => (order < 0 ? a[sortKey] - b[sortKey] : b[sortKey] - a[sortKey]))
    }
  },
  methods: {
    setTheUserToShowTasks (user) {
      if (this.allUsersInHash[user.id]) {
        this.theUserToShowTasks = this.allUsersInHash[user.id]
      }
    },
    closeTheUserToShowTasks () {
      this.theUserToShowTasks = null
    },
    numToFixed (num) {
      return parseFloat(num).toFixed(2)
    },
    createTooltip (sumForYear) {
      const hoursInYear = 222
      const hoursPerDay = sumForYear / hoursInYear
      const hoursForPeriod = hoursPerDay * this.numberOfWorkDaysInPeriod
      const hoursPerDayFixed = this.numToFixed(hoursPerDay)
      const hoursForPeriodFixed = this.numToFixed(hoursForPeriod)
      return `<p><i>Når du vælger et andet datointerval end hele året, kan du her se, hvor mange timer, der skal planlægges for medarbejderen i perioden og i gennemsnit per dag. Udregningen tager højde for institutionens ferie- og helligdage.</i></p><br></p><p><b>Beregning</b></p><p>For året: ${sumForYear} timer / ${hoursInYear} dage = ${hoursPerDayFixed} timer pr. dag</p><p>For perioden: ${this.numberOfWorkDaysInPeriod} arbejdsdage * ${hoursPerDayFixed} timer pr. dag = ${hoursForPeriodFixed} timer</p>`
    },
    getAssignedHoursForPeriod (hours) {
      if (this.isDatesPristine) {
        return hours
      } else {
        const hoursPerDay = hours / 222
        return hoursPerDay * this.numberOfWorkDaysInPeriod
      }
    },
    sortBy (key) {
      this.sortKey = key
      this.sortOrder = this.sortOrder * -1
    },
    setTheActiveUserInAssignHours (user) {
      this.setTheUserToShowTasks(user)
    },
    compareDates (firstDate, secondDate) {
      return moment(firstDate).format('YYYY-MM-DD') === moment(secondDate).format('YYYY-MM-DD')
    },
    scaleIndicator (diff) {
      let color = 'rgba(62, 207, 142, 0.4)'
      let newDiff = parseFloat(diff)
      let indicatorPercentage = parseFloat(newDiff.toFixed(0))
      if (indicatorPercentage < 10 && indicatorPercentage > 0) {
        indicatorPercentage = '0' + indicatorPercentage
      }
      if (indicatorPercentage > -10 && indicatorPercentage < 0) {
        indicatorPercentage = '0' + (indicatorPercentage * -1)
      }
      if (indicatorPercentage < -9 && indicatorPercentage < 0) {
        indicatorPercentage = indicatorPercentage * -1
      }
      if (newDiff === 0) {
        color = 'transparent'
      }
      if (newDiff < 0) {
        color = 'rgba(250, 86, 90, 0.' + indicatorPercentage + ')'
      }
      if (newDiff < -99) {
        color = 'rgba(250, 86, 90, 1)'
      }
      if (newDiff > 0) {
        color = 'rgba(62, 207, 142, 0.' + indicatorPercentage + ')'
      }
      if (newDiff > 99) {
        color = 'rgba(62, 207, 142, 1)'
      }
      return {'--bgScaleColor': color}
    },
    getUsersActualHours () {
      this.isLoadingUpdatedNumbers = true
      const startDate = moment(this.startDate).format('YYYY-MM-DD') + ' 00:00'
      const endDate = moment(this.endDate).format('YYYY-MM-DD') + ' 23:59'
      docendo.schoolActualHours(this.schoolId, startDate, endDate, null, data => {
        let newUsers = {}
        for (let i = 0; i < data.length; i++) {
          newUsers[data[i].user_id] = data[i]
        }
        this.usersWithScheduledHours = newUsers
        this.isLoadingUpdatedNumbers = false
      })
    },
    updateUserHours () {
      this.isLoadingUpdatedNumbers = true
      docendo.schoolRecalculateAll(this.schoolId, data => {
        this.getUsersActualHours()
      }, error => {
        console.log('error', error)
        this.isLoadingUpdatedNumbers = false
      })
    },
    setStartDate (date) {
      this.setDates(date, null)
    },
    setEndDate (date) {
      this.setDates(null, date)
    },
    resetDates () {
      if (this.activeSchoolYear) {
        this.setDates(this.activeSchoolYear.employee_time_start, this.activeSchoolYear.employee_time_end)
      }
    },
    setDatesFromRange (dateRange) {
      this.setDates(dateRange.start, dateRange.end)
    },
    setDates (start, end) {
      if (start) {
        this.startDate = start
      }
      if (end) {
        this.endDate = end
      }
      this.getUsersActualHours()
    },
    readVacationDays () {
      docendo.schoolVacationDaysRead(this.schoolId, success => {
        this.allVacationsDays = success
      }, failure => {
        console.log('failure')
      })
    }
  },
  beforeMount () {
    this.readVacationDays()
    this.resetDates()
  }
}
</script>

<style scoped>
@import '../../assets/css/variables.css';

.users-hours__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
}

.user-table-container {
  max-width: 100%;
  max-height: calc(100vh - 190px);
  overflow: scroll;
  position: relative;
  border-radius: 4px;
  box-shadow: 0 1px 2px rgb(0 0 0 / 7%);
  background: #fff;
}

.user-table-container__loader {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(237, 240, 245, 0.44);
  z-index: 121;
  cursor: progress;
}

.user-table {
  width: 100%;
  color: #3d4340;
  border-collapse: inherit;
  border-spacing: 0;
}

.user-table th {
  color: #3d4340;
  font-weight: bold;
  position: sticky;
  z-index: 1;
  top: 0;
}

.user-table thead tr:first-child th:first-child {
  padding-left: 20px;
  cursor: pointer;
}

.user-table tbody tr:nth-child(odd) {
  background-color: #f8fafb;
}

.user-table tbody tr td {
  padding: 0 18px;
  border-top: 1px solid #ffffff;
  border-bottom: 1px solid #e0e0e0;
  border-right: 1px solid #e0e0e0;
  text-align: center;
}

.user-table thead tr:first-child th:first-child,
.user-table tbody tr td:first-child {
  text-align: left;
  width: 200px;
}

.user-table th {
  border-collapse: collapse;
  border-bottom: 1px solid #d8d8d8;
  border-right: 1px solid #d8d8d8;
  padding: 4px;
  text-align: center;
}


.user-table tr:first-child th:nth-child(1) {
  border-right: none;
}

.user-table tr:first-child th:nth-child(2),
.user-table tr:nth-child(2) th:nth-child(1) {
  border-left: 1px solid #d8d8d8;
}

.working-hours,
.assigned_total_hours,
.actual_total_hours,
.diff_total_hours {
  background: #f6f9fe;
}


.tr-user {
  content-visibility: auto;
  contain-intrinsic-size: 1px 50px;
}

.tr-user:hover, .tr-user--active {
  border: 1px solid var(--blue);
  background: #eeeeee !important;
}

.tr-user.tr-user--active th, .tr-user:hover th {
  background: #eeeeee !important;
}

.tr-user th {
  position: sticky;
  z-index: 2;
  left: 0;
  border-collapse: collapse;
  background: #ffffff;
}

.tr-user:nth-child(odd) th {
  background: #f8fafb;
}

.number {
  position: relative;
  padding: 0 16px;
  text-align: right;
  border-bottom: 1px solid #d8d8d8 !important;
}

.number__scale-indicator:before {
  content: attr(aria-label);
  position: absolute;
  font-weight: normal;
  z-index: 0;
  top: 10%;
  left: 10%;
  right: 20%;
  bottom: 10%;
  width: 70%;
  height: 80%;
  background: var(--bgScaleColor);
  display: flex;
  align-items: center;
  justify-content: flex-end;
  border-radius: 12px;
  padding-right: 10%;
}

.tr-user {
  height: 50px;
  align-items: center;
}

.tr-user__name {
  text-align: left;
  width: 200px;
  border-collapse: collapse;
  border-bottom: 1px solid #d8d8d8;
  padding: 0.5rem 1rem;
}

.tr-user__name__inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.tr-user__name__inner > p {
  width: 165px;
  overflow: hidden;
  line-height: 1;
  text-align: left;
}

.tr-user__name__bttn {
  display: flex;
  align-items: center;
  justify-content: center;
}

.user-table__body tr td.td-presence,
.user-table__body tr td.td-presence,
.user-table__body tr td.td-presence {
  background: var(--presenceColor);
  border: 0;
}

.user-table__body tr td.td-teaching,
.user-table__body tr td.td-teaching,
.user-table__body tr td.td-teaching {
  background: var(--teachingColor);
  border: 0;
}

.user-table__body tr td.td-tasks,
.user-table__body tr td.td-tasks,
.user-table__body tr td.td-tasks {
  background: var(--taskColor);
  border: 0;
}

.user-table__body tr td.td-preparation,
.user-table__body tr td.td-preparation,
.user-table__body tr td.td-preparation {
  background: var(--prepTimeColor);
  border: 0;
}

.user-table__body tr td.td-absence {
  background: var(--absenceColor);
  border: 0;
}

.user-table__body tr td.td-sub-hours,
.user-table__body tr td.td-sub-hours,
.user-table__body tr td.td-sub-hours {
  background: var(--subColor);
  border: 0;
}

.user-table__body tr td.td-club,
.user-table__body tr td.td-club,
.user-table__body tr td.td-club {
  background: var(--clubColor);
  border: 0;
}

.scale-enter-active {
  transition: transform 0.15s;
}

.scale-enter, .scale-leave-to {
  transform: scale(0.5);
}

.tw-icon {
  width: 1.7rem;
}

.secondary {
  fill: #0D2B3E;
}

.primary {
  fill: #a5b3bb;
}

.number {
  position: relative;
}

.number:before {
  content: attr(aria-label);
  position: absolute;
  font-weight: normal;
  z-index: 0;
  top: 10%;
  left: 10%;
  right: 20%;
  bottom: 10%;
  width: 70%;
  height: 80%;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  border-radius: 12px;
  padding-right: 10%;
}


.user-task__container {
  height: calc(100vh - 2rem);
  width: calc(100vw - 2rem);
  max-width: 1440px;
  padding: 2rem;
  overflow-y: auto;
}

.user-task__container__header {
  margin: -1rem -2rem 1rem -2rem;
  border-radius: 4px 4px 0 0;
  display: flex;
  padding: 0 2rem;
  height: 2rem;
  justify-content: space-between;
  align-items: center;
}

.user-tasks__header__right {
  display: flex;
  align-items: center;
}

.user-tasks__header__right .d-link {
  color: var(--blue);
  cursor: pointer;
  margin-right: 1rem;
}

.user-tasks__header__right .d-link:hover {
  text-decoration: underline;
}

.marg-05-right {
  margin-right: 0.5rem;
}

.users-header-second__buttons {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.td-presence {
  background: var(--presenceColor);
  border: 0;
}

.with-kids-color {
  background: #feede8;
}

.without-kids-color {
  background: #f0f0f0;
}

.tr-user > td {
  text-align: right;
}
</style>



// WEBPACK FOOTER //
// AnnualNormResult.vue?0df0b370